"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { Phone, ShoppingBag, Loader2, CheckCircle2 } from "lucide-react";
import { useRouter } from "next/navigation";
import { Product } from "../types/public.types";
import { publicService } from "../services/publicService";
import { deduplicateProducts } from "../utils/publicUtils";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import LoginModal from "./LoginModal";

export default function FeaturedProducts() {
    const router = useRouter();
    const { user, activeRole } = useAuth();
    const [products, setProducts] = useState<Product[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

    const handlePhoneClick = (e: React.MouseEvent) => {
        const isAllowed = user && (activeRole === 'buyer' || activeRole === 'fpo_connect' || user.role === 'buyer' || user.role === 'fpo_connect');
        if (!isAllowed) {
            e.preventDefault();
            e.stopPropagation();
            setIsLoginModalOpen(true);
        } else {
            e.stopPropagation();
        }
    };

    const loadProducts = async (pageNum: number, isInitial = false) => {
        try {
            if (isInitial) {
                setIsLoading(true);
            } else {
                setIsFetchingMore(true);
            }

            const data = await publicService.getProducts({ page: pageNum, limit: 10 });
            const newProducts = data.products || [];
            const approvedProducts = newProducts.filter(
                (prod: Product) => prod.status === "approved"
            );

            if (isInitial) {
                setProducts(deduplicateProducts(approvedProducts));
            } else {
                setProducts((prev) => deduplicateProducts([...prev, ...approvedProducts]));
            }
            setTotal(data.total || 0);
        } catch (error) {
            console.error("Error loading products:", error);
        } finally {
            setIsLoading(false);
            setIsFetchingMore(false);
        }
    };

    useEffect(() => {
        loadProducts(1, true);
    }, []);

    const handleLoadMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        loadProducts(nextPage, false);
    };

    if (isLoading) {
        return (
            <section className="py-10 bg-gray-100 overflow-hidden">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <div className="h-8 w-64 bg-slate-350 animate-pulse rounded-sm mx-auto mb-3" />
                        <div className="h-4 w-96 bg-slate-350 animate-pulse rounded-sm mx-auto" />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-6">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                            <div
                                key={i}
                                className="bg-white rounded-lg border border-slate-200 p-3 sm:p-4 h-full min-h-[310px] sm:min-h-[350px] w-full max-w-[240px] mx-auto flex flex-col justify-between animate-pulse"
                            >
                                <div className="space-y-4 w-full">
                                    <div className="w-full h-[110px] sm:h-[130px] bg-slate-200 rounded-lg" />
                                    <div className="h-4 bg-slate-200 rounded-lg w-3/4 mx-auto" />
                                    <div className="h-3 bg-slate-200 rounded-lg w-1/2 mx-auto" />
                                    <div className="h-4 bg-slate-200 rounded-lg w-1/3 mx-auto" />
                                </div>
                                <div className="space-y-2 w-full mt-3">
                                    <div className="h-8 bg-slate-200 rounded-lg w-full" />
                                    <div className="h-8 bg-slate-200 rounded-lg w-full" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    if (products.length === 0) {
        return (
            <section className="py-10 bg-gray-100 overflow-hidden min-h-[400px] flex items-center justify-center">
                <div className="text-center">
                    <ShoppingBag className="w-16 h-16 text-green-600/50 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-slate-700">No Featured Products Yet</h2>
                    <p className="text-slate-500 mt-2">Check back soon for new arrivals!</p>
                </div>
            </section>
        );
    }

    return (
        <section className="py-10 bg-gray-100 overflow-hidden">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">

                {/* Section Header */}
                <div className="text-center max-w-2xl mx-auto mb-12">
                    <h2 className="text-2xl lg:text-3xl font-bold text-green-600 tracking-tight">
                        Our Featured <span className="text-black-650">Products</span>
                    </h2>
                    <p className="mt-3 text-sm sm:text-base text-slate-600 font-medium">
Discover selected agricultural products from registered manufacturers, suppliers, dealers, distributors and other vendors across the Agri-Input and Agri-Output categories.                    </p>
                    <div className="w-12 h-1 bg-green-600 mx-auto mt-4 rounded-full" />
                </div>

                {/* Products Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-6">
                    {products.map((prod) => {
                        const categorySlug = prod.category?.slug || "general";
                        const subcategorySlug = prod.subcategory?.slug || "uncategorized";
                        const productUrl = `/${categorySlug}/${subcategorySlug}/${prod.slug}`;

                        const activeVariant = prod.variants && prod.variants.length > 0 ? prod.variants[0] : null;
                        const salePrice = activeVariant ? activeVariant.salePrice : null;
                        const mrpPrice = activeVariant ? activeVariant.mrpPrice : null;
                        const variantName = activeVariant ? activeVariant.variant : "";

                        const priceString = salePrice !== null
                            ? (variantName ? `₹${salePrice} / ${variantName}` : `₹${salePrice}`)
                            : null;

                        return (
                            <div
                                key={prod._id}
                                onClick={() => router.push(productUrl)}
                                className="group bg-white rounded-lg border border-slate-200/85 hover:border-green-300 transition-all duration-300 hover:shadow-lg flex flex-col justify-between p-3 sm:p-4 h-full min-h-[310px] sm:min-h-[350px] w-full max-w-[240px] mx-auto text-center shadow-xs cursor-pointer"
                            >
                                <div className="flex flex-col flex-1">

                                    {/* Compact Rounded Image Frame */}
                                    <div className="relative w-full h-[110px] sm:h-[130px] bg-white rounded-lg overflow-hidden flex items-center justify-center p-2 border border-slate-100 mb-2">
                                        {prod.imageUrl ? (
                                            <Image
                                                src={prod.imageUrl}
                                                alt={prod.title}
                                                fill
                                                sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 240px"
                                                className="object-contain p-2 transition-transform duration-500 group-hover:scale-105"
                                            />
                                        ) : (
                                            <ShoppingBag className="w-10 h-10 sm:w-12 sm:h-12 text-slate-350" />
                                        )}
                                    </div>

                                    {/* Text Content */}
                                    <div className="flex flex-col items-center">
                                        {/* Title */}
                                        <h3 className="text-xs sm:text-sm md:text-base font-bold text-slate-900 group-hover:text-green-600 transition-colors duration-200 line-clamp-1 mb-0.5 sm:mb-1 capitalize px-1">
                                            {prod.title}
                                        </h3>

                                        {/* Vendor Name */}
                                        <span className="text-[11px] sm:text-xs md:text-[14px] font-semibold text-slate-500 mb-1 sm:mb-1.5 flex items-center justify-center gap-1 max-w-full">
                                            <span className="truncate">{prod.vendorDetails?.name || "Verified Vendor"}</span>
                                            {prod.vendorDetails?.isApproved && (
                                                <span title="Verified Vendor" className="inline-flex shrink-0">
                                                    <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-800 fill-green-40" />
                                                </span>
                                            )}
                                        </span>

                                        {/* Pricing */}
                                        {priceString ? (
                                            <span className="text-xs sm:text-sm md:text-base font-bold text-green-700 text-center">
                                                {priceString}
                                            </span>
                                        ) : (
                                            <span className="text-[10px] sm:text-xs font-bold text-slate-400 italic text-center">
                                                Call for Quote
                                            </span>
                                        )}

                                        {/* MRP Cross-Out */}
                                        {mrpPrice !== null && mrpPrice > (salePrice || 0) && (
                                            <span className="text-[10px] sm:text-xs font-semibold text-red-700 line-through text-center mt-0.5">
                                                ₹{mrpPrice}
                                            </span>
                                        )}

                                        {/* Stock Status */}
                                        <span className="text-[10px] sm:text-xs text-slate-500 text-center mt-0.5 sm:mt-1">
                                            In Stock ({variantName || "1"})
                                        </span>
                                    </div>
                                </div>

                                {/* Stacked Vertical Action Buttons */}
                                <div className="mt-2.5 sm:mt-3 flex flex-col gap-1.5 sm:gap-2 w-full">

                                    {/* View Number Button */}
                                    {prod.vendorDetails?.phoneNumber ? (
                                        <a
                                            href={`tel:${prod.vendorDetails.phoneNumber}`}
                                            onClick={handlePhoneClick}
                                            className="w-full border border-green-600 text-green-700 font-bold py-1.5 sm:py-2 px-2 sm:px-4 rounded-lg hover:bg-green-50 transition flex items-center justify-center gap-1.5 bg-white text-[10px] sm:text-xs cursor-pointer"
                                        >
                                            <Phone className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
                                            View Number
                                        </a>
                                    ) : (
                                        <span 
                                            onClick={handlePhoneClick}
                                            className="w-full border border-slate-200 text-slate-400 font-bold py-1.5 sm:py-2 px-2 sm:px-4 rounded-lg flex items-center justify-center gap-1.5 bg-slate-50 text-[10px] sm:text-xs cursor-pointer"
                                        >
                                            <Phone className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
                                            View Number
                                        </span>
                                    )}

                                    {/* Get Best Price Button */}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            router.push(productUrl);
                                        }}
                                        className="w-full bg-[#0b7033] hover:bg-[#074720] text-white font-bold py-1.5 sm:py-2 px-2 sm:px-4 rounded-lg transition duration-200 text-[10px] sm:text-xs text-center block cursor-pointer"
                                    >
                                        Get Best Price
                                    </button>
                                </div>

                            </div>
                        );
                    })}
                </div>

                {/* Load More Button */}
                {products.length < total && (
                    <div className="flex justify-center mt-10">
                        <button
                            onClick={handleLoadMore}
                            disabled={isFetchingMore}
                            className="inline-flex items-center gap-2 px-8 py-2.5 rounded-lg border border-green-600 hover:bg-green-50 text-green-700 bg-white font-bold transition duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-xs active:scale-95 text-xs sm:text-sm"
                        >
                            {isFetchingMore ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin text-green-650" />
                                    Loading...
                                </>
                            ) : (
                                "Load More"
                            )}
                        </button>
                    </div>
                )}
            </div>

            {/* Reusable Login Modal */}
            <LoginModal 
                isOpen={isLoginModalOpen} 
                onClose={() => setIsLoginModalOpen(false)} 
            />
        </section>
    );
}
